/**
 * AdminWebsiteConfig — FRD §29 (5 page sections) + §28 (academic year start/end)
 * Tabs: General | Academic | Home Page | About Us | Contact Us | Seal of Biliteracy | Social Media
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Grid, TextField, Button, Tabs, Tab, IconButton, Tooltip
} from "@mui/material";
import SettingsIcon        from "@mui/icons-material/Settings";
import SaveOutlinedIcon    from "@mui/icons-material/SaveOutlined";
import FacebookIcon        from "@mui/icons-material/Facebook";
import InstagramIcon       from "@mui/icons-material/Instagram";
import YouTubeIcon         from "@mui/icons-material/YouTube";
import LinkedInIcon        from "@mui/icons-material/LinkedIn";
import CloudUploadIcon     from "@mui/icons-material/CloudUpload";
import { fetchData, putData, postData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

/* ── Tab / Category definitions ── */
const CATEGORIES = ["general", "academic", "home", "about", "contact", "seal", "social"];
const CATEGORY_LABELS = {
  general:  "General Settings",
  academic: "Academic Year",
  home:     "Home Page",
  about:    "About Us",
  contact:  "Contact Us",
  seal:     "Seal of Biliteracy",
  social:   "Social Media",
};

/* ── Default config entries ── */
const DEFAULT_CONFIGS = [
  // General
  { configKey: "school_name",        configValue: "Cumming Tamil School",  label: "School Name",          category: "general" },
  { configKey: "school_email",       configValue: "",                       label: "School Email",         category: "general" },
  { configKey: "school_phone",       configValue: "",                       label: "School Phone",         category: "general" },
  { configKey: "school_address",     configValue: "Cumming, GA",            label: "School Address",       category: "general" },
  // Academic — §28
  { configKey: "academic_year_start",configValue: "2025-08-01",             label: "Academic Year Start",  category: "academic", fieldType: "date" },
  { configKey: "academic_year_end",  configValue: "2025-05-31",             label: "Academic Year End",    category: "academic", fieldType: "date" },
  { configKey: "academic_term_count",configValue: "3",                      label: "Number of Terms",      category: "academic" },
  // Home Page — §29
  { configKey: "home_hero_title",    configValue: "Welcome to Cumming Tamil School", label: "Hero Title",         category: "home" },
  { configKey: "home_hero_subtitle", configValue: "Nurturing Tamil culture & language", label: "Hero Subtitle",    category: "home" },
  { configKey: "home_hero_image_url",configValue: "",                       label: "Hero Image URL",       category: "home", imgKey: true },
  { configKey: "home_cta_text",      configValue: "Register Now",           label: "CTA Button Text",      category: "home" },
  { configKey: "home_cta_link",      configValue: "/student-registration",  label: "CTA Button Link",      category: "home" },
  // About Us — §29
  { configKey: "about_mission",      configValue: "",                       label: "Mission Statement",    category: "about", multiline: true },
  { configKey: "about_vision",       configValue: "",                       label: "Vision Statement",     category: "about", multiline: true },
  { configKey: "about_image_url",    configValue: "",                       label: "About Page Image URL", category: "about", imgKey: true },
  { configKey: "about_history",      configValue: "",                       label: "School History (short)", category: "about", multiline: true },
  // Contact Us — §29
  { configKey: "contact_address",    configValue: "Cumming, GA",            label: "Full Address",         category: "contact" },
  { configKey: "contact_maps_url",   configValue: "",                       label: "Google Maps Embed URL",category: "contact" },
  { configKey: "contact_hours",      configValue: "Saturdays 9AM – 1PM",   label: "Office Hours",         category: "contact" },
  { configKey: "contact_email",      configValue: "",                       label: "Contact Email",        category: "contact" },
  { configKey: "contact_phone",      configValue: "",                       label: "Contact Phone",        category: "contact" },
  // Seal of Biliteracy — §29
  { configKey: "seal_section_title", configValue: "Seal of Biliteracy",     label: "Section Title",        category: "seal" },
  { configKey: "seal_description",   configValue: "",                       label: "Description",          category: "seal", multiline: true },
  { configKey: "seal_image_url",     configValue: "",                       label: "Badge Image URL",      category: "seal", imgKey: true },
  { configKey: "seal_apply_link",    configValue: "",                       label: "Apply Now Link",       category: "seal" },
  { configKey: "seal_deadline",      configValue: "",                       label: "Application Deadline", category: "seal", fieldType: "date" },
  // Social
  { configKey: "facebook_url",       configValue: "",                       label: "Facebook URL",         category: "social" },
  { configKey: "instagram_url",      configValue: "",                       label: "Instagram URL",        category: "social" },
  { configKey: "twitter_url",        configValue: "",                       label: "Twitter/X URL",        category: "social" },
  { configKey: "youtube_url",        configValue: "",                       label: "YouTube URL",          category: "social" },
  { configKey: "linkedin_url",       configValue: "",                       label: "LinkedIn URL",         category: "social" },
];

const AdminWebsiteConfig = () => {
  const [tabValue, setTabValue] = useState(0);
  const [edited,   setEdited]   = useState({});
  const [toast,    setToast]    = useState({ open: false, severity: "success", message: "" });
  const [uploading, setUploading] = useState({});

  const buildDefaults = () => {
    const vals = {};
    DEFAULT_CONFIGS.forEach(c => { vals[c.configKey] = c.configValue; });
    return { vals };
  };

  const load = useCallback(async () => {
    try {
      const res = await fetchData("admin/website-config/list");
      if (res?.data) {
        const arr = Array.isArray(res.data) ? res.data : [];
        const { vals } = buildDefaults();
        arr.forEach(c => { vals[c.configKey] = c.configValue || ""; });
        setEdited(vals);
        return;
      }
    } catch (_) { /* fall through */ }
    const { vals } = buildDefaults();
    setEdited(vals);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSaveCategory = async (category) => {
    const categoryKeys = DEFAULT_CONFIGS.filter(c => c.category === category).map(c => c.configKey);
    const configsToUpdate = categoryKeys.map(key => ({ configKey: key, configValue: edited[key] || "" }));
    try {
      await putData("admin/website-config/bulk-update", { configs: configsToUpdate });
      setToast({ open: true, severity: "success", message: `${CATEGORY_LABELS[category]} saved successfully!` });
    } catch (_) {
      setToast({ open: true, severity: "error", message: "Failed to save. Please try again." });
    }
  };

  const handleImageUpload = async (configKey, file) => {
    if (!file) return;
    setUploading(prev => ({ ...prev, [configKey]: true }));
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("configKey", configKey);
      const res = await postData("admin/media/upload", form);
      const url = res?.data?.url || res?.url || "";
      if (url) {
        setEdited(prev => ({ ...prev, [configKey]: url }));
        setToast({ open: true, severity: "success", message: "Image uploaded" });
      }
    } catch (_) {
      setToast({ open: true, severity: "warning", message: "Upload failed — paste image URL manually" });
    }
    setUploading(prev => ({ ...prev, [configKey]: false }));
  };

  const categoryConfigs = (category) =>
    DEFAULT_CONFIGS.filter(c => c.category === category);

  const getSocialIcon = (key) => {
    if (key.includes("facebook"))  return <FacebookIcon  sx={{ color: "#1877F2" }} />;
    if (key.includes("instagram")) return <InstagramIcon sx={{ color: "#E1306C" }} />;
    if (key.includes("youtube"))   return <YouTubeIcon   sx={{ color: "#FF0000" }} />;
    if (key.includes("linkedin"))  return <LinkedInIcon  sx={{ color: "#0A66C2" }} />;
    return null;
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px", fontSize: "13px",
      "& fieldset": { borderColor: "#D5D7DA" },
      "&:hover fieldset": { borderColor: "#4A7BA8" },
      "&.Mui-focused fieldset": { borderColor: "#244C77" },
    },
  };

  return (
    <div style={{ textAlign: "left", padding: "1rem" }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity} message={toast.message} />

      <p style={{ color: "rgba(0,0,0,0.45)", fontWeight: "bold", fontSize: "14px", margin: "0 0 8px" }}>
        Pages / Website Configuration
      </p>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <SettingsIcon sx={{ color: "#244C77", fontSize: "28px" }} />
        <Box>
          <Typography sx={{ fontWeight: "bold", fontSize: "22px", color: "#111827" }}>Website Configuration</Typography>
          <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>Manage website settings and content</Typography>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: "16px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: "1px solid #E5E7EB", px: 2,
            "& .MuiTab-root": { textTransform: "none", fontSize: "13px", fontWeight: 500, color: "#6B7280", minWidth: 110 },
            "& .Mui-selected": { color: "#244C77 !important", fontWeight: "bold" },
            "& .MuiTabs-indicator": { backgroundColor: "#244C77" },
          }}>
          {CATEGORIES.map(cat => (
            <Tab key={cat} label={CATEGORY_LABELS[cat]} />
          ))}
        </Tabs>

        {CATEGORIES.map((category, idx) =>
          tabValue !== idx ? null : (
            <Box key={category} sx={{ p: 3 }}>
              <Grid container spacing={2.5}>
                {categoryConfigs(category).map(cfg => (
                  <Grid item xs={12} sm={cfg.multiline ? 12 : 6} key={cfg.configKey}>
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.8 }}>
                        {category === "social" && getSocialIcon(cfg.configKey)}
                        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#374151" }}>
                          {cfg.label}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                        <TextField
                          fullWidth size="small"
                          type={cfg.fieldType || "text"}
                          multiline={!!cfg.multiline}
                          rows={cfg.multiline ? 3 : undefined}
                          value={edited[cfg.configKey] ?? ""}
                          onChange={e => setEdited(prev => ({ ...prev, [cfg.configKey]: e.target.value }))}
                          placeholder={`Enter ${cfg.label}…`}
                          InputLabelProps={cfg.fieldType === "date" ? { shrink: true } : {}}
                          sx={fieldSx}
                        />
                        {cfg.imgKey && (
                          <Tooltip title="Upload image">
                            <IconButton
                              component="label"
                              disabled={uploading[cfg.configKey]}
                              sx={{ border: "1px solid #D5D7DA", borderRadius: "8px", p: 1, flexShrink: 0 }}
                            >
                              <CloudUploadIcon sx={{ color: "#244C77", fontSize: "20px" }} />
                              <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={e => handleImageUpload(cfg.configKey, e.target.files[0])}
                              />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Box sx={{ mt: 3, pt: 2.5, borderTop: "1px solid #F3F4F6", display: "flex", justifyContent: "flex-end" }}>
                <Button
                  startIcon={<SaveOutlinedIcon />}
                  onClick={() => handleSaveCategory(category)}
                  sx={{
                    background: "linear-gradient(90deg, #4A7BA8, #244C77)",
                    color: "#fff", fontSize: "13px", textTransform: "none",
                    borderRadius: "10px", px: 3, py: 1,
                    "&:hover": { background: "linear-gradient(90deg, #4A7BA8, #244C77)" },
                  }}
                >
                  Save {CATEGORY_LABELS[category]}
                </Button>
              </Box>
            </Box>
          )
        )}
      </Paper>
    </div>
  );
};

export default AdminWebsiteConfig;
